import React, { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Navigate, useNavigate } from 'react-router-dom';

import Modal from '@/ui-components/Modal';
import { Tab, TabContent, Tabs } from '@/ui-components/Tabs';
import Panel, { PanelClose } from '@/ui-components/Panel';
import Button from '@/ui-components/Button';
import ButtonGroup from '@/ui-components/ButtonGroup';
import Typography from '@/ui-components/Typography';
import ProfileScreen from '@/ui-components/ProfileScreen';
import SignupForm from '@/ui-components/Login/SignupForm';

import EditAccount from './EditAccount';
import ChangePassword from './ChangePassword';

import useBem from '@/use/useBem';

import { userSelector, logout, resendVerification, loadUser } from '@/framework/user';

const AccountModal = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState('profile');
  const [resending, setResending] = useState(false);
  const leaveToLogin = useRef(false);
  const { bem } = useBem('account');
  const loggedIn = useRecoilValue(userSelector.loggedIn);
  const user = useRecoilValue(userSelector.user);
  const emailVerified = Boolean(user.emailVerified);

  useEffect(() => {
    loadUser().catch(() => {
      // keep current session
    });
  }, []);

  useEffect(() => {
    if (!emailVerified && (value === 'edit' || value === 'password')) {
      setValue('profile');
    }
  }, [emailVerified, value]);

  const handleChange = (newValue: string) => {
    setValue(newValue);
  };

  const handleClose = () => {
    navigate('/');
  };

  const handleAfterClose = () => {
    setValue('profile');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleChangeUser = () => {
    if (emailVerified) {
      leaveToLogin.current = true;
      logout();
    }
    navigate('/login');
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification();
    } catch {
      // ignore
    } finally {
      setResending(false);
    }
  };

  if (!loggedIn) {
    return (
      <Navigate to={leaveToLogin.current ? '/login' : '/'} replace />
    );
  }

  return (
    <Modal
      isOpen={true}
      onRequestClose={handleClose}
      onAfterClose={handleAfterClose}
      contentClassName={bem('')}
    >
      <Panel>
        <PanelClose onClick={handleClose} />
        <Tabs className={bem('tabs')} value={value} onChange={handleChange}>
          <Tab value="profile">Profile</Tab>
          {emailVerified && <Tab value="edit">Edit details</Tab>}
          {emailVerified && <Tab value="password">Change password</Tab>}
        </Tabs>

        <ButtonGroup className={bem('actions')}>
          <Button size="medium" onClick={handleChangeUser}>
            Change user
          </Button>
          {emailVerified && (
            <Button size="medium" color="red" onClick={handleLogout}>
              Logout
            </Button>
          )}
        </ButtonGroup>

        <TabContent value={value} index="profile">
          <ProfileScreen {...{ user }} />

          {user.isGuest && (
            <div className={bem('guest-signup')}>
              <SignupForm />
            </div>
          )}

          {user.hasEmail && !emailVerified && (
            <div className={bem('verify')}>
              <Typography variant="m">Check your email</Typography>
              <ButtonGroup className={bem('buttons')}>
                <Button size="medium" onClick={handleResend} loading={resending}>
                  Resend verification email
                </Button>
              </ButtonGroup>
            </div>
          )}
        </TabContent>

        {emailVerified && (
          <TabContent value={value} index="edit">
            <EditAccount />
          </TabContent>
        )}

        {emailVerified && (
          <TabContent value={value} index="password">
            <ChangePassword />
          </TabContent>
        )}
      </Panel>
    </Modal>
  );
};

export default AccountModal;
